import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { BehaviorSubject, combineLatest, merge } from 'rxjs'
import { map, takeUntil, tap } from 'rxjs/operators'
import { type ControlSize, sizeClasses } from '@schmancy/theme/control-size'
import { FocusHandoff } from '@schmancy/utils/focus-handoff'
import '@schmancy/primitives/icons/icon'
import { chipTextChanges, removeName } from './remove-name'

/**
 * Input chip — a discrete piece of user-provided input (a tag, a selected
 * recipient, an applied filter) that can be removed.
 *
 * IMPORTANT: Per Material Design 3 specification, input chips do NOT have selected state.
 * They can only be removed, not toggled on/off.
 *
 * The chip body is not a control — no role, no tab stop, no accessible name of
 * its own — so the remove button is the chip's one interactive element rather
 * than a second one nested inside a first, which is the shape that leaves focus
 * order, activation and what a screen reader announces implementation-defined
 * (axe reports it as `nested-interactive`, serious). The body stays a pointer
 * target and nothing more: clicking it emits `click` and paints a ripple, and a
 * token whose body only carries text has nothing for a keyboard to activate.
 *
 * Keyboard: one tab stop per chip — the remove button, which Enter and Space
 * activate natively. Delete and Backspace remove from anywhere inside the chip,
 * which is now reachable, since the chip holds a focusable element again.
 *
 * @fires click - Pointer click on the chip body (value). Not keyboard-reachable.
 * @fires remove - Dispatched when the remove button is activated (value)
 *
 * @example
 * ```html
 * <schmancy-input-chip value="john@example.com" avatar="/avatars/john.jpg">
 *   John Doe
 * </schmancy-input-chip>
 * ```
 */
@customElement('schmancy-input-chip')
export class SchmancyInputChip extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: inline-block;
				outline: none;
				width: fit-content;
				min-width: fit-content;
			}

			:host([disabled]) {
				pointer-events: none;
			}

			button {
				font-family: inherit;
			}

			.avatar-img {
				width: 20px;
				height: 20px;
				object-fit: cover;
			}

			.ripple {
				position: absolute;
				border-radius: var(--schmancy-radius-circle, 50%);
				transform: scale(0);
				animation: ripple 600ms linear;
				background-color: rgba(0, 0, 0, 0.08);
				pointer-events: none;
			}

			@keyframes ripple {
				to {
					transform: scale(4);
					opacity: 0;
				}
			}

			/* State layer for M3 hover/focus/pressed states */
			.state-layer {
				position: absolute;
				inset: 0;
				border-radius: inherit;
				pointer-events: none;
				background-color: currentColor;
				opacity: 0;
				transition: opacity 200ms ease;
			}

			:host(:not([disabled])) .chip-container:hover .state-layer {
				opacity: 0.08;
			}

			:host(:not([disabled])) .chip-container:focus-visible .state-layer {
				opacity: 0.1;
			}

			:host(:not([disabled])) .chip-container:active .state-layer {
				opacity: 0.1;
			}
		`,
	]
	@property({ type: String, reflect: true })
	value: string = ''

	/** Material Symbols icon name. */
	@property({ type: String, reflect: true })
	icon: string = ''

	@property({ type: String, reflect: true })
	avatar: string = ''

	/** Defaults true (unlike other chip types) — input chips are removable by default. */
	@property({ type: Boolean, reflect: true })
	removable: boolean = true

	/**
	 * Replaces the remove control's whole accessible name, which is otherwise
	 * `chip.remove` — `Remove {{name}}` in English — filled with the text this
	 * chip renders, so a field of chips answers to one name each rather than all
	 * to `Remove`. Still replaced whole rather than composed from a translated
	 * verb: the subject rides inside a sentence the wording table owns, because
	 * `${verb} ${noun}` concatenated here inverts in most of the languages this
	 * library ships to. `value` names the chip only when it renders no text at
	 * all, since a chip's value is as often an opaque id as it is the text on it.
	 */
	@property({ type: String, attribute: 'remove-label' })
	removeLabel: string = ''

	@property({ type: Boolean, reflect: true })
	disabled: boolean = false

	@property({ type: Boolean, reflect: true })
	elevated: boolean = false

	/** Size from the shared control-size ladder. Default 'sm' = 40px / 14px. */
	@property({ reflect: true })
	size: ControlSize = 'sm'

	/**
	 * Where focus goes when removing this chip removes the remove control that
	 * held it — same contract as `schmancy-button`'s `focus-successor`. Unset,
	 * focus goes to the previous chip, or whatever precedes the row.
	 */
	@property({ attribute: 'focus-successor' })
	focusSuccessor?: string

	protected readonly focusHandoff = new FocusHandoff(this, () => this.focusSuccessor)

	private chipHover$ = new BehaviorSubject<boolean>(false)
	private removeHover$ = new BehaviorSubject<boolean>(false)
	private focused$ = new BehaviorSubject<boolean>(false)
	private pressed$ = new BehaviorSubject<boolean>(false)

	@state() uiState = {
		chipHover: false,
		removeHover: false,
		focused: false,
		pressed: false,
	}

	@state() private ripples: Array<{ x: number; y: number; id: number }> = []
	private nextRippleId = 0

	constructor() {
		super()
		try {
			this.internals = this.attachInternals()
		} catch {
			this.internals = undefined
		}
	}

	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	}

	static formAssociated = true
	internals: ElementInternals | undefined
	get form() {
		return this.internals?.form
	}

	protected updated(changed: Map<string, unknown>) {
		super.updated?.(changed)
		if (changed.has('value')) {
			this.internals?.setFormValue(this.value || null)
		}
	}

	connectedCallback() {
		super.connectedCallback()

		merge(
			combineLatest([this.chipHover$, this.removeHover$, this.focused$, this.pressed$]).pipe(
				map(([chipHover, removeHover, focused, pressed]) => ({
					chipHover,
					removeHover,
					focused,
					pressed,
				})),
				tap(state => {
					this.uiState = state
				}),
			),
			chipTextChanges(this).pipe(tap(() => this.requestUpdate())),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	private handleChipClick = (e: MouseEvent) => {
		if (this.disabled) return

		e.stopPropagation()

		const chip = this.shadowRoot?.querySelector('.chip-container') as HTMLElement
		if (chip) {
			const rect = chip.getBoundingClientRect()
			const x = e.clientX - rect.left
			const y = e.clientY - rect.top
			const id = this.nextRippleId++

			this.ripples = [...this.ripples, { x, y, id }]

			setTimeout(() => {
				this.ripples = this.ripples.filter(r => r.id !== id)
			}, 600)
		}

		this.dispatchEvent(
			new CustomEvent('click', {
				detail: { value: this.value },
				bubbles: true,
				composed: true,
			}),
		)
	}

	private handleRemove = (e: Event) => {
		if (this.disabled) return

		e.stopPropagation()

		this.dispatchEvent(
			new CustomEvent('remove', {
				detail: { value: this.value },
				bubbles: true,
				composed: true,
			}),
		)
	}

	// Fires for keys pressed on the remove button, the only focusable element the
	// chip has. Enter and Space are the button's own and are left to it — handling
	// Enter here too would run the body's click path off the same keystroke that
	// already removed the chip.
	private handleKeyDown = (e: KeyboardEvent) => {
		if (this.disabled) return

		if ((e.key === 'Delete' || e.key === 'Backspace') && this.removable) {
			e.preventDefault()
			this.handleRemove(e)
		}
	}

	private handleFocus = () => {
		this.focused$.next(true)
	}

	private handleBlur = () => {
		this.focused$.next(false)
	}

	protected render(): unknown {
		const hasLeadingIcon = this.avatar || this.icon

		const chipClasses = {
			'chip-container': true,
			'inline-flex': true,
			'items-center': true,
			'rounded-full': true,
			'cursor-default': true,
			'transition-colors': true,
			'duration-200': true,
			'select-none': true,
			'font-medium': true, // M3: 500 weight
			relative: true,
			'overflow-hidden': true,

			// Size ladder: height / font / horizontal-pad / gap from control-size
			[sizeClasses(this.size, 'chip')]: true,

			// Leading/trailing pad overrides when icon or remove button narrows the side
			'pl-2': hasLeadingIcon, // 8px with avatar/icon
			'pr-2': this.removable, // 8px with remove button

			'bg-surface-containerLow': true,
			'text-surface-onVariant': true,
			// Input chips are flat (no elevation) per M3

			'opacity-38': this.disabled, // M3 disabled opacity
			'cursor-not-allowed': this.disabled,
		}

		const removeButtonClasses = {
			// 24px is WCAG 2.2 SC 2.5.8's floor for a pointer target, and the box
			// has to carry it rather than a transparent reach over the chip body:
			// the body is a target of its own with a different action, so the
			// criterion subtracts whatever the two overlap.
			'size-6': true,
			flex: true,
			'items-center': true,
			'justify-center': true,
			'rounded-full': true,
			'transition-colors': true,
			'duration-200': true,
			'cursor-pointer': !this.disabled,
			'-mr-1': true, // adjust for visual balance

			'hover:bg-surface-containerHighest': !this.disabled,
			'opacity-50': this.disabled,

			// The chip's own ring, moved here from the container: the container is no
			// longer focusable, so the ring it declared could never paint, and this
			// is the element a Tab now lands on. The 2px offset still falls inside
			// the pill — a 24px box in a 40px chip has 8px of room on each side.
			'focus-visible:outline-solid': !this.disabled,
			'focus-visible:outline-2': !this.disabled,
			'focus-visible:outline-primary': !this.disabled,
			'focus-visible:outline-offset-2': !this.disabled,
		}

		return html`
			<div
				class=${this.classMap(chipClasses)}
				@click=${this.handleChipClick}
				@keydown=${this.handleKeyDown}
				@mouseenter=${() => this.chipHover$.next(true)}
				@mouseleave=${() => this.chipHover$.next(false)}
				@mousedown=${() => this.pressed$.next(true)}
				@mouseup=${() => this.pressed$.next(false)}
			>
				${this.avatar ? html` <img src=${this.avatar} alt="" class="avatar-img rounded-full size-5" /> ` : ''}
				${
					this.icon && !this.avatar
						? html` <schmancy-icon class="text-[18px] shrink-0">${this.icon}</schmancy-icon> `
						: ''
				}

				<span class="font-medium">
					<slot @slotchange=${() => this.requestUpdate()}></slot>
				</span>

				${
					this.removable
						? html`
								<button
									class=${this.classMap(removeButtonClasses)}
									@click=${this.handleRemove}
									@mouseenter=${() => this.removeHover$.next(true)}
									@mouseleave=${() => this.removeHover$.next(false)}
									@focus=${this.handleFocus}
									@blur=${this.handleBlur}
									aria-label=${this.removeLabel || removeName(this)}
									?disabled=${this.disabled}
								>
									<schmancy-icon class="text-[18px]">close</schmancy-icon>
								</button>
							`
						: ''
				}
				${this.ripples.map(
					ripple => html` <span class="ripple" style="left: ${ripple.x}px; top: ${ripple.y}px;"></span> `,
				)}

				<div class="state-layer"></div>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-input-chip': SchmancyInputChip
	}
}

export type InputChipClickEvent = { value: string }
export type InputChipRemoveEvent = { value: string }
